/**
 * Billing checkout result pages — success polls /api/billing/status until paid.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          setTimeout(() => navigate("/app"), 1800);
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        // ignore and retry
      }
      if (n < 8) {
        setTimeout(() => { setAttempts(n + 1); poll(n + 1); }, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--canvas)] px-6">
      <div className="max-w-md text-center" data-testid="billing-success-shell">
        <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-3">Stripe</p>
        {status === "paid" ? (
          <>
            <h1 className="font-display text-4xl md:text-5xl mb-4" data-testid="billing-success-title">You're in. Get back to it.</h1>
            <p className="text-[var(--ink-secondary)] mb-6">Payment confirmed. Taking you to your scripts…</p>
          </>
        ) : status === "expired" ? (
          <>
            <h1 className="font-display text-4xl mb-4">That session expired.</h1>
            <Link to="/account" className="btn-brand" data-testid="billing-success-retry">Try again</Link>
          </>
        ) : status === "timeout" ? (
          <>
            <h1 className="font-display text-4xl mb-4">Hmm, still verifying…</h1>
            <p className="text-[var(--ink-secondary)] mb-6">Stripe is taking a sec. You can check your account page in a minute.</p>
            <Link to="/account" className="btn-brand">Go to account</Link>
          </>
        ) : (
          <>
            <h1 className="font-display text-4xl mb-4" data-testid="billing-success-checking">Confirming payment…</h1>
            <p className="text-[var(--ink-secondary)]">Attempt {attempts + 1}</p>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--canvas)] px-6">
      <div className="max-w-md text-center" data-testid="billing-cancel-shell">
        <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-3">Checkout canceled</p>
        <h1 className="font-display text-4xl md:text-5xl mb-4">No charge. No worries.</h1>
        <p className="text-[var(--ink-secondary)] mb-6">Whenever you're ready, head back to your account.</p>
        <Link to="/account" className="btn-brand" data-testid="billing-cancel-back">Back to account</Link>
      </div>
    </div>
  );
}
